import { Alert, Box, Checkbox, Container, Divider, Grid, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react'
import SnackbarContext from '../contexts/SnackbarContext';
import { Trans, useTranslation } from 'react-i18next';
import { LoginForm, LoginPageContainer, LoginPageContainerOverlay } from '../styles/containers';
import LangSelector from '../lang/LangSelector';
import { GoogleLoginButton, GoogleLoginButtonTextContainer, GoogleLoginIconContainer, LoginButton } from '../styles/buttons';
import Footer from '../footer/Footer';
import { validateEmail } from '../models/validate';
import { primary } from '../styles/colors';
import { LoginTextField } from '../styles/inputs';
import { postCreateRestaurant } from '../config/apiRoutes';
import { useNavigate } from 'react-router';
import { homePageRoute, loginPageRoute, menuEditorRoute, termsAndConditionsRoute, usePathReconstructor } from '../config/pageRoutes';
import { Link } from 'react-router-dom';
import GoogleIcon from '@mui/icons-material/Google';
import AuthContext, { AuthState } from '../contexts/AuthContext';


// Add gtag to the Window interface for TypeScript
declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
    }
}

type Props = {}

const SelfSignupPage = (props: Props) => {
    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [confirmPasswordError, setConfirmPasswordError] = useState("");

    const [showEmailSentMessage, setShowEmailSentMessage] = React.useState(false);
    const { googleLogin, authState, user } = useContext(AuthContext);
    const snackbar = useContext(SnackbarContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { copyParamsFromPath } = usePathReconstructor();
    const [termsAccepted, setTermsAccepted] = useState(false);

    useEffect(() => {
        document.title = `Quickpick ${t("navigation.signup")}`;
    }, []);

    function handleNameChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        setName(e.target.value);
    }

    function handleEmailChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        setEmail(e.target.value);
    }

    function handlePasswordChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        setPassword(e.target.value);
    }

    function handleConfirmPasswordChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        setConfirmPassword(e.target.value);
    }

    useEffect(() => {
        if (name.length < 2) {
            setNameError(t("auth.name_missing"));
        } else {
            setNameError("");
        }
    }, [name]);

    useEffect(() => {
        if (!validateEmail(email)) {
            setEmailError(t("auth.invalid_email"));
        } else {
            setEmailError("");
        }
    }, [email]);

    useEffect(() => {
        if (password.length < 8) {
            setPasswordError(t("auth.password_min_length"));
        } else {
            setPasswordError("");
        }
    }, [password]);

    useEffect(() => {
        if (confirmPassword === "" || confirmPassword !== password) {
            setConfirmPasswordError(t("auth.passwords_do_not_match"));
        } else {
            setConfirmPasswordError("");
        }
    }, [confirmPassword, password]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (hasErrors()) return;

        const response = await fetch(postCreateRestaurant, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                name: name,
                email: email,
                password: password,
            })
        });
        if (response.ok) {
            //@ts-ignore
                if (window.gtag) {
                //@ts-ignore
                    window.gtag('event', 'sign_up', {
                        method: 'email',
                        page_location: window.location.href
                    });
                }
            setShowEmailSentMessage(true);
        } else {
            snackbar.showMessage(t("auth.signup_failed"), "error");
        }
    }

    function hasErrors() {
        return nameError.length > 0
            || emailError.length > 0
            || passwordError.length > 0
            || confirmPasswordError.length > 0
            || !termsAccepted;
    }

    useEffect(() => {
        if (authState === AuthState.LOGGED_IN) {
            navigate(copyParamsFromPath(menuEditorRoute.path));
        }
    }, [authState, user]);

    return (
        <>
            <LoginPageContainer>
                <LoginPageContainerOverlay>
                    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
                        <Box pt={2} px={4}>
                            <Grid container>
                                <Grid item>
                                    <Link to={copyParamsFromPath(homePageRoute)}>
                                        <img
                                            src={process.env.REACT_APP_PUBLIC_URL + '/assets/logo_white.svg'}
                                            width={"80px"}
                                        />
                                    </Link>
                                </Grid>
                                <Grid item xs>
                                </Grid>
                                <Grid item>
                                    <Box pt={1}>
                                        <LangSelector />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                        <Container style={{ flex: "1", maxWidth: "1600px" }}>
                            <Box pt={3}>
                                <Grid container style={{ justifyContent: "center" }}>
                                    <Grid xs item style={{ maxWidth: "500px" }}>
                                        <LoginForm px={3} pt={3} pb={8}>
                                            <Typography
                                                fontWeight={"bold"}
                                                fontSize="30px"
                                                mt={1}
                                                fontFamily="Avenir Next"
                                                color={primary.deep}
                                                textAlign={"center"}
                                            >
                                                {t("auth.qp_signup_title")}
                                            </Typography>
                                            {showEmailSentMessage && (
                                                <Alert severity="success">
                                                    {t("auth.activation_mail_sent")}
                                                </Alert>
                                            )}
                                            {!showEmailSentMessage && (
                                                <>
                                                    <Typography py={2}>
                                                        {t("auth.qp_signup_text")}
                                                    </Typography>
                                                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
                                                        <Typography fontSize={"1.4rem"} pb={1} >
                                                            {t("auth.user_information")}
                                                        </Typography>
                                                        <Box py={1}>
                                                            <LoginTextField
                                                                label={t("auth.name")}
                                                                type="text"
                                                                name="name"
                                                                fullWidth
                                                                value={name}
                                                                onChange={handleNameChange}
                                                                error={nameError.length > 0}
                                                                helperText={nameError}
                                                            />
                                                        </Box>
                                                        <Box py={1}>
                                                            <LoginTextField
                                                                label={t("auth.email")}
                                                                type="email"
                                                                name="email"
                                                                fullWidth
                                                                value={email}
                                                                onChange={handleEmailChange}
                                                                error={emailError.length > 0}
                                                                helperText={emailError}
                                                            />
                                                        </Box>
                                                        <Box py={1}>
                                                            <LoginTextField
                                                                label={t("auth.password")}
                                                                type="password"
                                                                name="password"
                                                                fullWidth
                                                                value={password}
                                                                onChange={handlePasswordChange}
                                                                error={passwordError.length > 0}
                                                                helperText={passwordError}
                                                            />
                                                        </Box>
                                                        <Box py={1}>
                                                            <LoginTextField
                                                                label={t("auth.confirm_password")}
                                                                type="password"
                                                                fullWidth
                                                                name="confirm-password"
                                                                value={confirmPassword}
                                                                onChange={handleConfirmPasswordChange}
                                                                error={confirmPasswordError.length > 0}
                                                                helperText={confirmPasswordError}
                                                            />
                                                        </Box>
                                                        <Box py={1}>
                                                            <div className='flex'>
                                                                <div>
                                                                    <Checkbox
                                                                        checked={termsAccepted}
                                                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <Trans
                                                                        i18nKey={"auth.accept_terms_and_conditions"}
                                                                        components={{ a: <a target="_blank" className="text-primary hover:underline" href={copyParamsFromPath(termsAndConditionsRoute.path)} /> }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Box>
                                                        <LoginButton
                                                            type="submit"
                                                            disabled={hasErrors()}
                                                        >
                                                            {t("auth.create_profile")}
                                                        </LoginButton>
                                                    </Box>
                                                    <Box>
                                                        <Box mb={3} mt={6} >
                                                            <Divider><b className="text-primary-dark">{t("common.or").toUpperCase()}</b></Divider>
                                                        </Box>
                                                        <Box>
                                                            <GoogleLoginButton type="button" onClick={googleLogin}>
                                                                <div className="flex">
                                                                    <div className="grow flex items-center pl-3 md:pl-6 pl-3 py-1">
                                                                        <GoogleLoginButtonTextContainer>
                                                                            {t("auth.login_with_google")}
                                                                        </GoogleLoginButtonTextContainer>
                                                                    </div>
                                                                    <div className="flex items-center">
                                                                        <GoogleLoginIconContainer>
                                                                            <GoogleIcon />
                                                                        </GoogleLoginIconContainer>
                                                                    </div>
                                                                </div>
                                                            </GoogleLoginButton>
                                                        </Box>
                                                    </Box>
                                                </>
                                            )}

                                        </LoginForm>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Container>
                        <Footer />
                    </div>
                </LoginPageContainerOverlay>
            </LoginPageContainer>
        </>
    )
}

export default SelfSignupPage;